import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { PaymentMethodModel } from '../../../../customer/customer.model';
import { GridColumnActionType } from '../../../../../shared/components/grid/grid-config';
import { AccountCardView } from './account-card-view';

export interface AccountCardItem {
  method: PaymentMethodModel;
  view: AccountCardView;
}

/**
 * One saved account (card or bank account), drawn as a card.
 *
 * Shared by Collect Payment's "which account" step and the customer detail view's Accounts on
 * File tab, so the two cannot drift. On Collect Payment the card is a radio to choose the account
 * being charged (`selectable`); on the customer it is a display with the same footer — default
 * star or "set as default", and the row actions menu.
 *
 * Purely presentational: it says what was clicked and the host decides what that means. Styles
 * are the global `.account-card` / `.account-picker` rules.
 */
@Component({
  selector: 'app-account-card',
  standalone: true,
  imports: [NgTemplateOutlet, MatButtonModule, MatIconModule, MatMenuModule, MatTooltipModule],
  templateUrl: './account-card.component.html',
  // `contents`, so the card itself — not this host element — is the grid item, exactly as when
  // the markup sat inline in the picker's grid.
  host: { style: 'display: contents' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccountCardComponent {
  @Input({ required: true }) item!: AccountCardItem;
  /** Render the body as a radio that chooses this account. */
  @Input() selectable = false;
  @Input() selected = false;
  @Input() actions: GridColumnActionType<PaymentMethodModel>[] = [];

  @Output() choose = new EventEmitter<PaymentMethodModel>();
  @Output() setDefault = new EventEmitter<PaymentMethodModel>();
  @Output() action = new EventEmitter<GridColumnActionType<PaymentMethodModel>>();
}
